// Leveraged LP Farming service — isolated per-user positions. Each market has a position
// factory; each user gets their own position contract (their own Blend borrower), so a
// liquidation only ever touches that user's collateral. Testnet-first.
// See soroban-contracts/leverage-vault (factory, position, zapper) and docs/leverage/.
//
// Mirrors the simulate → assembleTransaction → sign → send → poll flow used by
// SorobanVaultService, but network-aware (testnet/mainnet) via SOROBAN_CONFIG.

import {
  Address,
  Contract,
  rpc,
  TransactionBuilder,
  nativeToScVal,
  scValToNative,
} from "@stellar/stellar-sdk";
import {
  StellarWalletsKit,
  WalletNetwork,
  FREIGHTER_ID,
  LOBSTR_ID,
  FreighterModule,
  LobstrModule,
} from "@creit.tech/stellar-wallets-kit";
import { WALLET_CONNECT_ID } from "@creit.tech/stellar-wallets-kit/modules/walletconnect.module";
import { kit as walletConnectKit, reconnectWalletConnect } from "../components/Navbar";
import { SOROBAN_CONFIG } from "../config/soroban.config";

// Leverage farming is TESTNET-ONLY (Blend mainnet pool creation needs a backstop deposit),
// so this whole service is pinned to testnet regardless of the app's global network — the
// hidden /leverage screen "switches to testnet" while the rest of the app stays mainnet.
const TESTNET_RPC = "https://soroban-testnet.stellar.org";
const TESTNET_PASSPHRASE = "Test SDF Network ; September 2015";

// Stroop scaling — AQUA / XLM / LP share tokens all use 7 decimals.
const SCALAR_7 = 1e7;
/** Human amount → i128 stroops. Truncates past 7 decimals (never rounds up past a balance). */
const toI128 = (human: string | number) => {
  const v = parseFloat(String(human));
  if (!Number.isFinite(v) || v < 0) throw new Error(`Invalid amount: ${human}`);
  return nativeToScVal(BigInt(Math.floor(v * SCALAR_7)), { type: "i128" });
};

const isWCConnectionError = (e: any): boolean => {
  const msg = String(e?.message || e).toLowerCase();
  return (
    msg.includes("connection key") ||
    msg.includes("session") ||
    msg.includes("not connected") ||
    msg.includes("disconnected") ||
    msg.includes("no matching key")
  );
};

/** Which contracts a call touches, so an error code can be attributed to the contract that raised it. */
export interface ErrorContext {
  position?: string | null;
  zapper?: string;
  amm?: string;
  blendPool?: string;
}

/**
 * Turn a simulation error into something a user can act on. Error codes are only meaningful
 * per contract (the zapper, the AMM and the position all reuse small numbers), so the code is
 * read together with the contract id from the diagnostic event that raised it.
 */
function friendlyError(raw: string, ctx: ErrorContext = {}): string {
  const text = String(raw);
  const events = Array.from(
    text.matchAll(/contract:(C[A-Z2-7]{55}), topics:\[error, Error\(Contract, #(\d+)\)\]/g)
  ).map((m) => ({ contract: m[1], code: Number(m[2]) }));
  // The event log is printed newest first, so the last entry is where the failure started.
  const origin = events.length ? events[events.length - 1] : null;
  if (origin) {
    if (ctx.position && origin.contract === ctx.position && POSITION_ERRORS[origin.code]) {
      return POSITION_ERRORS[origin.code];
    }
    if (ctx.amm && origin.contract === ctx.amm && origin.code === 4) {
      return "The pool price moved since your quote. Try again or raise slippage tolerance.";
    }
    if (ctx.zapper && origin.contract === ctx.zapper && origin.code === 7) {
      return "The pool price moved since your quote. Try again or raise slippage tolerance.";
    }
    if (ctx.blendPool && origin.contract === ctx.blendPool && origin.code === 1205) {
      return "Blend rejected the position: the health factor would be too low.";
    }
    return `Transaction would fail (contract ${origin.contract.slice(0, 6)}…, error #${origin.code}).`;
  }
  return `Simulation failed: ${text}`;
}

export interface LeverageMarketConfig {
  blendPool: string;
  zapper: string;
  lpToken: string;
  borrowAsset: string;
  lpReserveIndex: number;
  borrowReserveIndex: number;
  maxLeverageBps: number;
  /** Minimum health factor after any open/withdraw, bps (14000 = 1.40). */
  minOpenHfBps: number;
  /** Below this health factor the manager (keeper) may deleverage, bps. */
  deleverageHfBps: number;
  manager: string;
  paused: boolean;
}

/** Live state of one user's isolated position (human units; values in the oracle's base). */
export interface LeverageHealth {
  collateralLp: number;
  debt: number;
  collateralValue: number;
  debtValue: number;
  /** null when there is no debt (health is unbounded). */
  healthFactor: number | null;
  /** null when the position is underwater (collateral value ≤ debt value). 1.0 with no debt. */
  leverage: number | null;
}

export interface TxResult {
  success: boolean;
  error?: string;
  transactionHash?: string;
}

const I128_MAX = BigInt("170141183460469231731687303715884105727");

/** Contract error codes of the position contract, for readable toasts. */
const POSITION_ERRORS: Record<number, string> = {
  3: "Invalid amount.",
  4: "That would exceed the market's leverage cap.",
  5: "That would leave your health factor below the market minimum.",
  6: "This market is paused for new positions. Repay, withdraw and deleverage still work.",
  7: "You have no position yet.",
  8: "Not enough collateral.",
  9: "Only the position owner can do that.",
  11: "That would not reduce your risk.",
  12: "The price oracle has no price for an asset right now.",
};

export class SorobanLeverageService {
  private server: rpc.Server;
  private sendServer: rpc.Server;
  private factoryId: string;
  private networkPassphrase: string;

  private dummyAccountCache: any = null;
  private dummyAccountFetchedAt = 0;

  constructor() {
    this.server = new rpc.Server(TESTNET_RPC);
    this.sendServer = new rpc.Server(TESTNET_RPC);
    this.factoryId = SOROBAN_CONFIG.leverage.markets[0]?.factory ?? "";
    this.networkPassphrase = TESTNET_PASSPHRASE;
  }

  /** True when at least one leverage market is configured. */
  isConfigured(): boolean {
    return Boolean(this.factoryId);
  }

  /** Point the service at a market's position factory (called when the user switches markets). */
  setMarket(factoryId: string): void {
    this.factoryId = factoryId;
  }

  // ----------------------------------------------------------------- internals

  private async withRetry<T>(fn: () => Promise<T>, maxRetries = 2): Promise<T> {
    let lastError: any;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (e: any) {
        lastError = e;
        const msg = String(e?.message || e);
        if (msg.includes("HostError") || msg.includes("Simulation failed")) throw e;
        if (attempt < maxRetries) await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
    }
    throw lastError;
  }

  private async getDummyAccount() {
    const now = Date.now();
    if (this.dummyAccountCache && now - this.dummyAccountFetchedAt < 5 * 60 * 1000) {
      return this.dummyAccountCache;
    }
    const account = await this.withRetry(() =>
      this.server.getAccount("GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF")
    );
    this.dummyAccountCache = account;
    this.dummyAccountFetchedAt = now;
    return account;
  }

  /** Read-only contract call against an arbitrary contract id. */
  private async readCallOn<T>(contractId: string, method: string, ...args: any[]): Promise<T> {
    const account = await this.getDummyAccount();
    const contract = new Contract(contractId);
    const tx = new TransactionBuilder(account, {
      fee: "100000",
      networkPassphrase: this.networkPassphrase,
    })
      .addOperation(contract.call(method, ...args))
      .setTimeout(60)
      .build();

    const sim = await this.withRetry(() => this.server.simulateTransaction(tx));
    if (rpc.Api.isSimulationError(sim)) {
      throw new Error(`Simulation failed (${method}): ${sim.error}`);
    }
    const retval = (sim as rpc.Api.SimulateTransactionSuccessResponse).result?.retval;
    return scValToNative(retval as any) as T;
  }

  /** Read-only call on a market's factory (defaults to the selected market). */
  private async readFactory<T>(factoryId: string | undefined, method: string, ...args: any[]): Promise<T> {
    const id = factoryId || this.factoryId;
    if (!id) throw new Error("Leverage market not configured");
    return this.readCallOn<T>(id, method, ...args);
  }

  private async buildSignKit(walletName: string): Promise<StellarWalletsKit> {
    if (walletName === WALLET_CONNECT_ID || walletName === ("wallet_connect" as any)) {
      await walletConnectKit.setWallet(WALLET_CONNECT_ID);
      return walletConnectKit;
    }
    const selectedModule = walletName === LOBSTR_ID ? new LobstrModule() : new FreighterModule();
    const walletId = walletName === LOBSTR_ID ? LOBSTR_ID : FREIGHTER_ID;
    const kit = new StellarWalletsKit({
      network: WalletNetwork.TESTNET,
      selectedWalletId: walletId,
      modules: [selectedModule],
    });
    await kit.setWallet(walletId);
    return kit;
  }

  /** Build → simulate → assemble → sign → send → poll for a state-changing call. */
  private async invoke(
    contractId: string,
    userAddress: string,
    walletName: string,
    method: string,
    args: any[],
    errorCtx: ErrorContext = {}
  ): Promise<TxResult> {
    try {
      if (!contractId) throw new Error("Leverage market not configured");
      let signKit = await this.buildSignKit(walletName);

      const contract = new Contract(contractId);
      const account = await this.withRetry(() => this.server.getAccount(userAddress));

      let tx = new TransactionBuilder(account, {
        fee: "1000000",
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(contract.call(method, ...args))
        .setTimeout(300)
        .build();

      const sim = await this.withRetry(() => this.server.simulateTransaction(tx));
      if (rpc.Api.isSimulationError(sim)) {
        throw new Error(friendlyError(sim.error, { position: contractId, ...errorCtx }));
      }
      tx = rpc.assembleTransaction(tx, sim).build();

      const txXdr = tx.toXDR();
      const signOpts = { address: userAddress, networkPassphrase: this.networkPassphrase };
      let signedTxXdr: string;
      try {
        ({ signedTxXdr } = await signKit.signTransaction(txXdr, signOpts));
      } catch (signErr: any) {
        const isWC = walletName === WALLET_CONNECT_ID || walletName === ("wallet_connect" as any);
        if (isWC && isWCConnectionError(signErr)) {
          signKit = reconnectWalletConnect();
          await signKit.setWallet(WALLET_CONNECT_ID);
          ({ signedTxXdr } = await signKit.signTransaction(txXdr, signOpts));
        } else {
          throw signErr;
        }
      }

      const signedTx = TransactionBuilder.fromXDR(signedTxXdr, this.networkPassphrase);
      const sendResponse = await this.sendServer.sendTransaction(signedTx as any);
      if (sendResponse.status === "PENDING") {
        return await this.pollTransactionResult(sendResponse.hash);
      }
      if (sendResponse.status === "ERROR") {
        throw new Error(`Transaction send error: ${JSON.stringify((sendResponse as any).errorResult ?? "")}`);
      }
      throw new Error(`Unexpected send status: ${sendResponse.status}`);
    } catch (error: any) {
      console.error(`[Leverage] ${method} error:`, error);
      return { success: false, error: error.message || `${method} failed` };
    }
  }

  private async pollTransactionResult(hash: string): Promise<TxResult> {
    // 60 attempts × 1s — matches the vault service's busy-ledger budget.
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const res = await this.server.getTransaction(hash);
        if (res.status === "SUCCESS") return { success: true, transactionHash: hash };
        if (res.status === "FAILED") {
          return { success: false, error: "Transaction failed on-chain", transactionHash: hash };
        }
      } catch {
        // NOT_FOUND yet — keep polling.
      }
    }
    return { success: false, error: "Timed out waiting for confirmation", transactionHash: hash };
  }

  // -------------------------------------------------------------------- reads

  async getMarketConfig(factoryId?: string): Promise<LeverageMarketConfig | null> {
    try {
      const m = await this.readFactory<any>(factoryId, "get_market");
      return {
        blendPool: m.blend_pool,
        zapper: m.zapper,
        lpToken: m.lp_token,
        borrowAsset: m.borrow_asset,
        lpReserveIndex: Number(m.lp_reserve_index),
        borrowReserveIndex: Number(m.borrow_reserve_index),
        maxLeverageBps: Number(m.max_leverage_bps),
        minOpenHfBps: Number(m.min_open_hf_bps),
        deleverageHfBps: Number(m.deleverage_hf_bps),
        manager: m.manager,
        paused: Boolean(m.paused),
      };
    } catch (e) {
      console.error("[Leverage] getMarketConfig failed:", e);
      return null;
    }
  }

  /** Position contracts ever deployed on this market (closed ones included). */
  async getPositionCount(factoryId?: string): Promise<number> {
    try {
      return Number(await this.readFactory<any>(factoryId, "get_position_count"));
    } catch {
      return 0;
    }
  }

  /**
   * The user's own position contract on this market: an address, null if they have none yet,
   * or undefined if it could not be read (so an RPC hiccup is not shown as "no position").
   */
  async getPositionAddress(userAddress: string, factoryId?: string): Promise<string | null | undefined> {
    try {
      const addr = await this.readFactory<any>(
        factoryId,
        "get_position",
        nativeToScVal(Address.fromString(userAddress), { type: "address" })
      );
      return addr ? String(addr) : null;
    } catch (e) {
      console.error("[Leverage] getPositionAddress failed:", e);
      return undefined;
    }
  }

  /** Live collateral, debt, health factor and leverage, computed on-chain by the position. */
  async getHealth(positionAddress: string): Promise<LeverageHealth | null> {
    try {
      const h = await this.readCallOn<any>(positionAddress, "get_health");
      const hf = BigInt(h.hf_bps);
      const lev = BigInt(h.leverage_bps);
      return {
        collateralLp: Number(h.collateral) / SCALAR_7,
        debt: Number(h.debt) / SCALAR_7,
        collateralValue: Number(h.collateral_value) / SCALAR_7,
        debtValue: Number(h.debt_value) / SCALAR_7,
        healthFactor: hf >= I128_MAX ? null : Number(hf) / 10_000,
        leverage: lev >= I128_MAX ? null : Number(lev) / 10_000,
      };
    } catch (e) {
      console.error("[Leverage] getHealth failed:", e);
      return null;
    }
  }

  /** null when it could not be read — the toggle is then disabled rather than guessed. */
  async isManagerEnabled(positionAddress: string): Promise<boolean | null> {
    try {
      return Boolean(await this.readCallOn<any>(positionAddress, "is_manager_enabled"));
    } catch {
      return null;
    }
  }

  /**
   * Quote the zap the vault performs inside the flash loan, against LIVE AMM reserves.
   *
   * This mirrors the on-chain arithmetic exactly — integer division included — so the
   * `min_lp_out` we send is a floor the contract can actually meet:
   *   1. `half = borrow / 2` is swapped borrow→pair   (constant product, 0.3% fee)
   *   2. the remaining borrow + the pair received are deposited
   *   3. shares minted = min(amt0·total/res0', amt1·total/res1')  — post-swap reserves
   *
   * Getting this wrong is not cosmetic: the old placeholder assumed `lpOut ≈ borrow`,
   * roughly double the truth, so `min_shares` could never be satisfied and every open
   * reverted with SlippageExceeded.
   */
  async getZapQuote(
    ammId: string,
    borrowAmountHuman: string,
    borrowIdx = 0
  ): Promise<{ pairOut: number; lpOut: number } | null> {
    try {
      const ZERO = BigInt(0);
      const borrow = BigInt(Math.round(parseFloat(borrowAmountHuman || "0") * SCALAR_7));
      if (borrow <= ZERO) return null;

      const [reserves, totalShares] = await Promise.all([
        this.readCallOn<any[]>(ammId, "get_reserves"),
        this.readCallOn<any>(ammId, "get_total_shares"),
      ]);
      const pairIdx = borrowIdx === 0 ? 1 : 0;
      const resIn = BigInt(reserves[borrowIdx]);
      const resOut = BigInt(reserves[pairIdx]);
      const total = BigInt(totalShares);
      if (resIn <= ZERO || resOut <= ZERO || total <= ZERO) return null;

      // 1. swap half, 0.3% fee (997/1000), constant product
      const half = borrow / BigInt(2);
      const inWithFee = (half * BigInt(997)) / BigInt(1000);
      const pairOut = (resOut * inWithFee) / (resIn + inWithFee);

      // 2. deposit against POST-swap reserves
      const newResIn = resIn + half;
      const newResOut = resOut - pairOut;
      const amtIn = borrow - half;
      const s0 = (amtIn * total) / newResIn;
      const s1 = (pairOut * total) / newResOut;
      const lpOut = s0 < s1 ? s0 : s1;

      return { pairOut: Number(pairOut) / SCALAR_7, lpOut: Number(lpOut) / SCALAR_7 };
    } catch (e) {
      console.error("[Leverage] getZapQuote failed:", e);
      return null;
    }
  }

  /** Blend collateral factor of the LP reserve and liability factor of the borrow reserve. */
  async getReserveFactors(
    blendPool: string,
    lpToken: string,
    borrowAsset: string
  ): Promise<{ cFactor: number; lFactor: number } | null> {
    try {
      const [lp, br] = await Promise.all([
        this.readCallOn<any>(blendPool, "get_reserve", nativeToScVal(Address.fromString(lpToken), { type: "address" })),
        this.readCallOn<any>(blendPool, "get_reserve", nativeToScVal(Address.fromString(borrowAsset), { type: "address" })),
      ]);
      return { cFactor: Number(lp.config.c_factor) / SCALAR_7, lFactor: Number(br.config.l_factor) / SCALAR_7 };
    } catch (e) {
      console.error("[Leverage] getReserveFactors failed:", e);
      return null;
    }
  }

  /** Value of one LP share in borrow-asset units, from live AMM reserves (both legs at pool price). */
  async getLpValueInBorrow(ammId: string, borrowIdx = 0): Promise<number | null> {
    try {
      const [reserves, totalShares] = await Promise.all([
        this.readCallOn<any[]>(ammId, "get_reserves"),
        this.readCallOn<any>(ammId, "get_total_shares"),
      ]);
      const total = Number(totalShares);
      if (!(total > 0)) return null;
      return (2 * Number(reserves[borrowIdx])) / total;
    } catch {
      return null;
    }
  }

  /**
   * Quote what `deleverage` realizes: withdraw `lpAmount` from the AMM into both legs, swap the
   * pair leg into the borrow asset (0.3% fee). Mirrors the zapper's `unzap`.
   */
  async getUnzapQuote(ammId: string, lpAmountHuman: string, borrowIdx = 0): Promise<number | null> {
    try {
      const ZERO = BigInt(0);
      const lp = BigInt(Math.round(parseFloat(lpAmountHuman || "0") * SCALAR_7));
      if (lp <= ZERO) return null;
      const [reserves, totalShares] = await Promise.all([
        this.readCallOn<any[]>(ammId, "get_reserves"),
        this.readCallOn<any>(ammId, "get_total_shares"),
      ]);
      const pairIdx = borrowIdx === 0 ? 1 : 0;
      const rB = BigInt(reserves[borrowIdx]);
      const rP = BigInt(reserves[pairIdx]);
      const total = BigInt(totalShares);
      if (total <= ZERO) return null;
      const outB = (rB * lp) / total;
      const outP = (rP * lp) / total;
      const rB2 = rB - outB;
      const rP2 = rP - outP;
      const inWithFee = (outP * BigInt(997)) / BigInt(1000);
      const swapped = (rB2 * inWithFee) / (rP2 + inWithFee);
      return Number(outB + swapped) / SCALAR_7;
    } catch (e) {
      console.error("[Leverage] getUnzapQuote failed:", e);
      return null;
    }
  }

  // ------------------------------------------------------------------- writes

  /** Deploy the user's isolated position contract on this market (once per user). */
  async createPosition(userAddress: string, walletName: string, factoryId?: string): Promise<TxResult> {
    return this.invoke(factoryId || this.factoryId, userAddress, walletName, "create_position", [
      nativeToScVal(Address.fromString(userAddress), { type: "address" }),
    ]);
  }

  /**
   * Open / add to the user's leveraged position. `minLpOut` / `minPairOut` should come from
   * `getZapQuote` (live AMM reserves) with the user's slippage tolerance applied. The position
   * contract rejects the result if it breaks the leverage cap or the minimum health factor.
   */
  async openPosition(params: {
    positionAddress: string;
    userAddress: string;
    walletName: string;
    collateralLpAmount: string;
    borrowAmount: string;
    minLpOut: string;
    minPairOut: string;
    errorCtx?: ErrorContext;
  }): Promise<TxResult> {
    const { positionAddress, userAddress, walletName, collateralLpAmount, borrowAmount, minLpOut, minPairOut, errorCtx } = params;
    return this.invoke(
      positionAddress,
      userAddress,
      walletName,
      "open",
      [toI128(collateralLpAmount), toI128(borrowAmount), toI128(minLpOut), toI128(minPairOut)],
      errorCtx
    );
  }

  /** Repay debt from the wallet and/or withdraw LP collateral to the wallet. */
  async repayAndWithdraw(params: {
    positionAddress: string;
    userAddress: string;
    walletName: string;
    repayAmount: string;
    withdrawLpAmount: string;
  }): Promise<TxResult> {
    const { positionAddress, userAddress, walletName, repayAmount, withdrawLpAmount } = params;
    return this.invoke(positionAddress, userAddress, walletName, "repay_and_withdraw", [
      toI128(repayAmount),
      toI128(withdrawLpAmount),
    ]);
  }

  /** Self-funded deleverage: sell `lpAmount` of collateral into the borrow asset and repay. */
  async deleverage(params: {
    positionAddress: string;
    userAddress: string;
    walletName: string;
    lpAmount: string;
    minBorrowOut: string;
    errorCtx?: ErrorContext;
  }): Promise<TxResult> {
    const { positionAddress, userAddress, walletName, lpAmount, minBorrowOut, errorCtx } = params;
    return this.invoke(
      positionAddress,
      userAddress,
      walletName,
      "deleverage",
      [nativeToScVal(Address.fromString(userAddress), { type: "address" }), toI128(lpAmount), toI128(minBorrowOut)],
      errorCtx
    );
  }

  /** Allow or revoke WhaleHub's keeper right to deleverage this position. */
  async setManagerEnabled(params: {
    positionAddress: string;
    userAddress: string;
    walletName: string;
    enabled: boolean;
  }): Promise<TxResult> {
    const { positionAddress, userAddress, walletName, enabled } = params;
    return this.invoke(positionAddress, userAddress, walletName, "set_manager_enabled", [
      nativeToScVal(enabled),
    ]);
  }
}

export const sorobanLeverageService = new SorobanLeverageService();
